import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { XMarkIcon, TrashIcon, ShoppingBagIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { useCart } from '../context/CartContext'
import { getShippingInfo, UNITS_PER_BOX, getLinePrice } from '../data/products'
import BulkOffersBanner from './BulkOffersBanner'

const CartDrawer = () => {
  const { cartItems, removeFromCart, updateQuantity, subtotal, totalBoxes, hasFreeEngraving, hasFreeBox, isCartOpen, setIsCartOpen } = useCart()
  const navigate = useNavigate()
  const shipping = getShippingInfo(totalBoxes)

  const [hasDiscount, setHasDiscount] = React.useState(() => localStorage.getItem('twiggle_claimed_first_order_discount') === 'true')

  React.useEffect(() => {
    const checkDiscount = () => {
      setHasDiscount(localStorage.getItem('twiggle_claimed_first_order_discount') === 'true')
    }
    window.addEventListener('storage', checkDiscount)
    checkDiscount()
    return () => window.removeEventListener('storage', checkDiscount)
  }, [isCartOpen])

  const discountAmount = hasDiscount ? subtotal * 0.10 : 0
  const finalTotal = Math.max(0, subtotal - discountAmount + shipping.cost)

  const handleCheckout = () => {
    setIsCartOpen(false)
    navigate('/checkout')
  }

  if (!isCartOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        id="cart-drawer"
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
        role="dialog"
        aria-label="Shopping cart"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ShoppingBagIcon className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-bold text-gray-900">Your Cart</h2>
            {totalBoxes > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                {totalBoxes} {totalBoxes === 1 ? 'box' : 'boxes'}
              </span>
            )}
          </div>
          <button
            id="close-cart-btn"
            onClick={() => setIsCartOpen(false)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close cart"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {cartItems.length > 0 && <BulkOffersBanner className="mb-2" />}
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <ShoppingBagIcon className="h-16 w-16 text-gray-200 mb-4" />
              <p className="text-gray-500 font-medium">Your cart is empty</p>
              <p className="text-gray-400 text-sm mt-1">Add some eco-friendly toothbrushes!</p>
              <button
                onClick={() => { setIsCartOpen(false); navigate('/shop') }}
                className="mt-6 px-6 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors"
              >
                Browse Products
              </button>
            </div>
          ) : (
            cartItems.map((item, index) => (
              <div key={index} className="flex gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
                {/* Product Image */}
                <div className="h-20 w-20 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-gray-100">
                  <img
                    src={item.product.image}
                    alt={item.product.fullName}
                    className="h-full w-full object-contain p-2"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {item.product.fullName}
                    {item.isFreeGift && (
                      <span className="ml-1 text-xs font-bold text-green-600">FREE</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {item.quantity * UNITS_PER_BOX} toothbrushes
                  </p>
                  {item.subscription && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                      Subscription: {item.subscription.label}
                    </span>
                  )}
                  {item.engravingData?.file && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                      Custom engraving{hasFreeEngraving ? ' (free)' : ''}
                    </span>
                  )}
                  {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(item.selectedOptions).map(([name, opt]) => (
                        <span key={name} className="inline-block text-[10px] px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full font-semibold">
                          {name}: {opt.value}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    {/* Qty selector */}
                    <div className="flex items-center gap-2">
                      <button
                        disabled={item.isFreeGift}
                        onClick={() => updateQuantity(index, item.quantity - 1)}
                        className="w-7 h-7 rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold flex items-center justify-center"
                        aria-label="Decrease quantity"
                      >−</button>
                      <span className="text-sm font-semibold text-gray-900 w-6 text-center">{item.quantity}</span>
                      <button
                        disabled={item.isFreeGift}
                        onClick={() => updateQuantity(index, item.quantity + 1)}
                        className="w-7 h-7 rounded-full bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-bold flex items-center justify-center"
                        aria-label="Increase quantity"
                      >+</button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-900">
                        ${getLinePrice(item).toFixed(2)}
                      </span>
                      <button
                        onClick={() => removeFromCart(index)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove item"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer with Totals */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-100 p-5 space-y-3 bg-white">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({totalBoxes} boxes)</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {hasDiscount && (
                <div className="flex justify-between text-green-600 font-semibold">
                  <span>First Order Discount (-10%)</span>
                  <span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span className={`font-medium ${shipping.cost === 0 ? 'text-green-600' : ''}`}>
                  {shipping.cost === 0 ? 'Free' : `$${shipping.cost.toFixed(2)}`}
                </span>
              </div>
              {shipping.cost > 0 && (
                <p className="text-xs text-gray-400">{shipping.note}</p>
              )}
              <div className="pt-2 border-t border-gray-100 flex justify-between font-bold text-gray-900 text-base">
                <span>Total</span>
                <span>${finalTotal.toFixed(2)} CAD</span>
              </div>
            </div>
            <button
              id="cart-checkout-btn"
              onClick={handleCheckout}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors duration-200"
            >
              Proceed to Checkout
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setIsCartOpen(false); navigate('/shop') }}
              className="w-full py-2.5 px-6 text-sm text-primary-600 hover:text-primary-700 font-medium text-center transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default CartDrawer
